import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Avatar, Button, TextField, IconButton, Chip,
  CircularProgress, Alert, Divider, Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import useDmsPalette from '../hooks/useDmsPalette';

export default function CommentThread({ documentId }) {
  const MP = useDmsPalette();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadComments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.getComments(documentId);
      setComments(data.comments || data || []);
    } catch (err) {
      setError(err.error || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { loadComments(); }, [loadComments]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await dmsApi.addComment(documentId, { body: newComment.trim() });
      setNewComment('');
      loadComments();
    } catch (err) {
      setError(err.error || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (commentId) => {
    try {
      await dmsApi.resolveComment(documentId, commentId);
      loadComments();
    } catch (err) {
      setError(err.error || 'Failed to resolve comment');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
  };

  // Group comments by parentId for threading
  const topLevel = comments.filter(c => !c.parentId);
  const replies = comments.filter(c => c.parentId);
  const getReplies = (parentId) => replies.filter(r => r.parentId === parentId);

  const renderComment = (comment, isReply = false) => {
    const resolved = comment.resolved;
    return (
      <Box
        key={comment._id}
        sx={{
          ml: isReply ? 5 : 0,
          opacity: resolved ? 0.5 : 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Avatar
            sx={{
              width: 28, height: 28, fontSize: '0.75rem',
              bgcolor: resolved ? MP.textSecondary : MP.blue,
            }}
          >
            {(comment.author?.name || comment.author?.email || 'U')[0].toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                {comment.author?.name || comment.author?.email || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {comment.createdAt ? format(new Date(comment.createdAt), 'MMM d, yyyy · h:mm a') : ''}
              </Typography>
              {resolved && (
                <Chip label="Resolved" size="small" icon={<CheckCircleIcon />}
                  sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${MP.green}20`, color: MP.green }}
                />
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: MP.white,
                textDecoration: resolved ? 'line-through' : 'none',
                lineHeight: 1.5,
              }}
            >
              {comment.body}
            </Typography>
            {!isReply && !resolved && (
              <Tooltip title="Resolve thread">
                <Button
                  size="small" startIcon={<CheckCircleIcon />}
                  onClick={() => handleResolve(comment._id)}
                  sx={{ mt: 0.5, fontSize: '0.7rem', color: MP.textSecondary }}
                >
                  Resolve
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
        {/* Render replies */}
        {!isReply && getReplies(comment._id).map(reply => renderComment(reply, true))}
      </Box>
    );
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {topLevel.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: MP.textSecondary }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="body2">No comments yet. Start the conversation.</Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          {topLevel.map(comment => renderComment(comment))}
        </Box>
      )}

      <Divider sx={{ borderColor: MP.border, my: 2 }} />

      {/* New comment form */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth multiline minRows={1} maxRows={4}
          placeholder="Write a comment... (Ctrl+Enter to send)"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: MP.deepBlue } }}
        />
        <IconButton
          color="primary" onClick={handleSubmit}
          disabled={!newComment.trim() || submitting}
          sx={{ bgcolor: MP.blue, color: MP.white, '&:hover': { bgcolor: `${MP.blue}CC` }, '&.Mui-disabled': { bgcolor: MP.surface } }}
        >
          {submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}
